import React, { useContext, useState, useCallback, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form } from 'antd';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { FormInputNew } from 'components/Input/FormInputNew';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalSentSuccessfullyNew } from 'components/ModalWindows/ModalSentSuccessfullyNew';
import Text from 'components/Text/Text';
import { AuthLayout } from '../components/AuthLayout';
import style from '../components/AuthFormCard.module.scss';

type ValidateStatus = Parameters<typeof Form.Item>[0]['validateStatus'];
type ComponentState = 'waitingDataEntry' | 'waitingServerResponse' | 'successServerResponse' | 'errorServerResponse';

interface IFormStatus {
	componentState: ComponentState;
	validateStatus?: ValidateStatus;
	errorMessage?: string | null;
	enteredEmail?: string;
}

type TEmail = {
	email: string;
};

export const PasswordResetPage: React.FC = () => {
	const { passwordReset } = useContext<IAppContext>(AppContext);
	const [userAuthStatus, setUserAuthStatus] = useState<IFormStatus>({ componentState: 'waitingDataEntry' });
	const [emailWithoutSpaces, setEmailWithoutSpaces] = useState<string>('');
	const { t: tLogin } = useTranslation('login');
	const { t: tGlobal } = useTranslation('global');
	const history = useHistory();

	const goToLogInPage = useCallback(() => {
		history.push('/');
	}, []);

	const handleRemindPassword = async (email: TEmail) => {
		setUserAuthStatus({ componentState: 'waitingServerResponse' });
		const userStatus = await passwordReset(email.email);
		if (userStatus === 'emailExists') {
			setUserAuthStatus({ componentState: 'successServerResponse', enteredEmail: email.email });
		} else {
			setUserAuthStatus({
				validateStatus: 'error',
				errorMessage: tLogin('validation.emailNotFound'),
				componentState: 'errorServerResponse',
			});
		}
	};

	const handleFieldsErrorReset = () => {
		const shouldCleanError = Object.keys(userAuthStatus).includes('validateStatus');
		if (shouldCleanError) {
			setUserAuthStatus({ componentState: 'waitingDataEntry' });
		}
	};

	useEffect(() => {
		const crutch = emailWithoutSpaces.replace(/\s/g, '');
		setEmailWithoutSpaces(crutch);
	}, [emailWithoutSpaces]);

	const isSendDisabled =
		userAuthStatus.componentState === 'waitingServerResponse' ||
		userAuthStatus.componentState === 'errorServerResponse';

	return (
		<>
			{userAuthStatus.componentState === 'successServerResponse' && (
				<ModalWrapper isVisible={true}>
					<ModalSentSuccessfullyNew
						onConsentEvent={goToLogInPage}
						description="emailSentSuccessfully"
						additionalDescription="passwordResetInstructionsSent"
					/>
				</ModalWrapper>
			)}
			<AuthLayout dataTest="PasswordReset">
				<div className={style.formCard}>
					<Text translationKey={tLogin('passwordReset.title')} size="xl" weight="bold" />
					<Form
						name="basic"
						colon={false}
						className={style.formBody}
						onFinish={handleRemindPassword}
						onChange={handleFieldsErrorReset}
						fields={[
							{
								name: ['email'],
								value: emailWithoutSpaces,
							},
						]}
						layout="vertical"
						autoComplete="off"
						data-test="form">
						<div className={style.fieldsGroup}>
							<FormInputNew
								name="email"
								label="email"
								translationNameSpace="login"
								placeholder="email"
								validateStatus={userAuthStatus.validateStatus}
								help={userAuthStatus.errorMessage}
								validationRules={[
									{ required: true, message: tLogin('validation.enterCorrectValue.email') },
								]}
								onChange={(value) => setEmailWithoutSpaces(value.replace(/\s/g, ' '))}
							/>
						</div>

						<div className={style.actionsRow} data-test="btns">
							<NewButton variant="outline" color="gray" size="small" onClick={goToLogInPage}>
								{tGlobal('cancel')}
							</NewButton>
							<NewButton
								type="submit"
								variant="primary"
								color="purple"
								size="small"
								disabled={isSendDisabled}
								loading={userAuthStatus.componentState === 'waitingServerResponse'}>
								{tLogin('passwordReset.sendMail')}
							</NewButton>
						</div>
					</Form>
				</div>
			</AuthLayout>
		</>
	);
};
